import { useFieldArray, useFormContext } from "react-hook-form";
import { Card } from "react-bootstrap";
import { DomainButton } from "../../../../_components/Abstractions/DomainButton";
import { BiMinus } from "react-icons/bi";
import { HorizontalView } from "../../../../_components/Abstractions/HorizontalView";
import { FormControl } from "../../../../_components/Forms/FormControl";
import { NumericControl } from "../../../../_components/Forms/NumericControl";
import { WeighbridgeTicketWasteDetailsForm } from "./WeighbridgeTicketWasteDetailsForm";
import { WeighbridgeTicketRejectedWasteDetailsForm } from "./WeighbridgeTicketRejectedWasteDetailsForm";
import { createEmptyTicket } from "./WeighbridgeTicketsFormProvider";
import { PlusCircleFill } from "react-bootstrap-icons";
import { Dropdown } from "../../../../_components/Abstractions/Dropdown";
import { WASTE_EXCHANGE_TYPE, WasteExchangeType } from "../../../../domain/WasteExchangeType";
import { DomainSwitch } from "../../../../_components/Abstractions/DomainSwitch";
import { ExternalWeighingTicketSelect } from "../../../../_components/Orders/ExternalWeighingTickets/ExternalWeighingTicketSelect";
import { WeighbridgeTicketExternalDataInfo } from "./WeighbridgeTicketExternalDataInfo";
import { useEffect } from "react";
import { WasteExchangeContainerSelect } from "../../../../_components/Containers/WasteExchangeContainerSelect";

export function WeighbridgeTicketsFormControls() {
  const { fields, append, remove } = useFieldArray({ name: "weighbridgeTickets" });
  const { getValues, formState: { errors } } = useFormContext();

  const handleTicketsSelected = (tickets) => {
    tickets.forEach((ticket) => append(ticket));
  };

  const orderContext = getValues("orderContext");
  const wasteExchanges = orderContext?.wasteExchanges;
  const weighbridgeTicketsRootError = errors?.weighbridgeTickets?.message;

  return (
    <div className="d-flex flex-column align-items-stretch">
      <div className="mb-2">
        <ExternalWeighingTicketSelect
          onTicketsSelected={handleTicketsSelected}
          wasteExchanges={wasteExchanges}
          externalContractorIds={orderContext?.externalContractorIds}
        />
      </div>
      {weighbridgeTicketsRootError && (
        <div className="text-danger mb-2">{weighbridgeTicketsRootError}</div>
      )}
      {fields.map((field, index) => (
        <WeightBridgeTicketFormControls
          key={field.id}
          ticketIndex={index}
          onRemove={() => remove(index)}
          wasteExchanges={wasteExchanges}
        />
      ))}
      <div className="d-flex gap-2 align-self-end mb-2">
        <DomainButton onClick={() => append(createEmptyTicket())}>
          Kwit wagowy <PlusCircleFill/>
        </DomainButton>
      </div>
      <hr/>
      <div className="d-flex gap-2 align-self-end mt-2">
        <DomainSwitch name="overrideWasteDetails" label="Przelicz towary na podstawie kwitów"/>
        <DomainSwitch name="bypassTicketDuplicationValidation" label="Zezwól na podpięcie kwitów do wielu zleceń"/>
      </div>
    </div>
  );
}

export const WeightBridgeTicketFormControls = ({ ticketIndex, onRemove, wasteExchanges }) => {
  const { watch, getValues, setValue, formState } = useFormContext();
  const getFieldNameForItem = (name) => `weighbridgeTickets[${ticketIndex}].${name}`;
  const exchangeType = watch(getFieldNameForItem("exchangeType"));
  const { isDirty } = formState;
  const externalTicketId = watch(getFieldNameForItem("externalTicketId"));
  const isProtocolNeeded = watch(getFieldNameForItem("isProtocolNeeded"));
  const isProtocolCompleted = getValues(getFieldNameForItem("isProtocolCompleted"));
  const orderContext = getValues("orderContext");
  const isExternalTicket = !!externalTicketId;
  const { setProtocolCompleted } = orderContext;
  const isReadonly = setProtocolCompleted && !isProtocolNeeded;

  useEffect(() => {
    if(isProtocolNeeded === true && isProtocolCompleted === true) {
      setValue(getFieldNameForItem("isProtocolCompleted"), false);
    }
  }, [isProtocolNeeded]);

  useEffect(() => {
    if(!isDirty) {
      return;
    }

    if (exchangeType === WASTE_EXCHANGE_TYPE.LOADING) {
      const loadingWasteExchange = wasteExchanges?.find(x => x.isContainer === false && x.usedContainerId === null);
      setValue(getFieldNameForItem("loadingWasteExchangeId"), loadingWasteExchange?.wasteExchangeId);
      setValue(getFieldNameForItem("wasteExchange"), null);
    } else {
      setValue(getFieldNameForItem("loadingWasteExchangeId"), null);
    }
  }, [exchangeType]);

  return (
    <Card className="mb-2">
      <Card.Header className="d-flex align-items-center justify-content-between">
        {`${ticketIndex + 1}. Kwit wagowy`}
        <div className="d-flex gap-3">
          <DomainSwitch name={getFieldNameForItem("isZeroWeighedTicket")} className="" label="Zerowy kwit" readonly={setProtocolCompleted}/>
          <DomainSwitch name={getFieldNameForItem("isProtocolNeeded")} className="" label="Protokół" readonly={setProtocolCompleted}/>
        </div>
        {isProtocolCompleted && <span className="text-primary">Protokół zatwierdzony</span>}
        <DomainButton size="small" onClick={onRemove}><BiMinus/> Usuń</DomainButton>
      </Card.Header>
      <Card.Body>
        <div
          className={isReadonly ? "pe-none user-select-none" : undefined}
          style={isReadonly ? { opacity: 0.6 } : undefined}
        >
          <HorizontalView>
            <FormControl name={getFieldNameForItem("ticketNumber")} label="Numer kwitu" readonly={isExternalTicket}/>
            <NumericControl decimalPlaces={4} name={getFieldNameForItem("netMass")} label="Masa netto [Mg]" readonly={isExternalTicket}/>
          </HorizontalView>
          <HorizontalView>
            <Dropdown label="Rozładunek" items={WasteExchangeType} name={getFieldNameForItem("exchangeType")} />
            {exchangeType === WASTE_EXCHANGE_TYPE.CONTAINER_EXCHANGE &&
             <WasteExchangeContainerSelect wasteExchanges={wasteExchanges} saveWholeObject name={getFieldNameForItem("wasteExchange")}/>}
          </HorizontalView>
          <FormControl name={getFieldNameForItem("remarks")} label="Uwagi" required={false}/>
          <WeighbridgeTicketExternalDataInfo ticketIndex={ticketIndex} />
          <WeighbridgeTicketWasteDetailsForm ticketIndex={ticketIndex} addressId={orderContext.addressId} transactionType={orderContext.transactionType}/>
          <WeighbridgeTicketRejectedWasteDetailsForm ticketIndex={ticketIndex}/>
        </div>
      </Card.Body>
    </Card>);
}

